import type { AgentBody, ResolvedAgent } from '@ralph/contracts'
import clsx from 'clsx'
import { TriangleAlert } from 'lucide-react'
import { useState } from 'react'
import type { AgentLayer } from '@/api/agents.ts'
import { Notice } from '@/components/ui/admin-chrome.tsx'
import { Badge, EmptyState, Panel, Spinner } from '@/components/ui/primitives.tsx'
import { useResolvedAgent } from '@/hooks/use-agents.ts'
import { describeApiError, type I18n, type Messages, useI18n, useT } from '@/i18n/index.ts'

/**
 * The merged agent, beside the definition that produced it.
 *
 * Every field carries where its value came from — the definition, the platform's own default,
 * or the runtime policy's ceiling — computed from the stored body rather than asked for, because
 * the merge policy is "the last layer that DEFINES the field wins", the same rule the server
 * applies. It is the same for everyone who can use the agent (PRD-26 D-23), so there is no
 * "preview as a member of…" any more: there is nothing a group could change.
 *
 * It sticks beside the editor only from `lg` up. Below that the two columns are stacked,
 * and a panel pinned to the top of a stacked column follows you over the fields you are
 * editing instead of sitting next to them.
 */

/**
 * The most specific layer before `before` that defines a field, or null.
 *
 * Shared with the editor, which asks the same question one layer at a time to show what a
 * blank field would inherit. Iterating backwards is the merge order read in reverse.
 */
export const inheritedFrom = <T,>(
  layers: AgentLayer[],
  read: (body: AgentBody) => T | undefined,
  before: number
): { value: T; scopeId: string } | null => {
  for (let i = Math.min(before, layers.length) - 1; i >= 0; i -= 1) {
    const layer = layers[i]
    if (!layer) continue
    const value = read(layer.body)
    if (value !== undefined) return { value, scopeId: layer.scopeId }
  }
  return null
}

/**
 * The formatters a row may use, handed to it at render time.
 *
 * `SECTIONS` below is a module-scope table and must stay one — it is the reading order of the
 * panel, not state — so the rows take the reader's formatters as an argument rather than
 * closing over them. A capture at import would freeze the locale of whoever loaded the bundle
 * first and re-render every number in it unchanged after a switch (PRD-27 F-3).
 *
 * `locale` rides along because two of the catalogue entries a row reaches count something,
 * and choosing between "1 pinned" and "2 pinned" is `Intl.PluralRules`' job rather than a
 * comparison the catalogue could make for itself — Portuguese takes the singular at zero.
 */
type RowFormat = Pick<I18n, 'locale' | 'formatCost' | 'formatCount' | 'formatDuration' | 'formatList' | 'formatNumber'>

/** The studio's words. Handed to a row at render time, never imported by the table below. */
type StudioPack = Messages['admin']['studio']
type SectionKey = keyof StudioPack['preview']['sections']
type RowKey = keyof StudioPack['preview']['rows']

type Row = {
  /**
   * The row's label, as a KEY rather than as the label itself.
   *
   * `SECTIONS` is a module-scope table and must stay one — it is the reading order of the
   * panel, not state — so the words cannot live in it: a catalogue read at import freezes the
   * locale of whoever loaded the bundle first and then renders that language forever, with no
   * type error and no failing test (PRD-27 F-3). `RowKey` is derived from the catalogue, so a
   * row whose label nobody wrote is a compile error here.
   */
  key: RowKey
  value: (agent: ResolvedAgent, fmt: RowFormat, t: StudioPack) => string
  /** Absent when no agent layer can express the field — it is the policy's alone. */
  reads?: (body: AgentBody) => unknown
  /** Prefixes concatenate across layers, so naming a single winner would be a lie. */
  concatenated?: boolean
  /**
   * Where the value comes from when it is not simply "the last layer that set it".
   *
   *  - `policy`: no layer can set this at all. Retrieval tuning and approval settings are
   *    RuntimePolicy values now (AGM-100), so attributing them to a layer would be a lie.
   *  - `ceiling`: the policy states a maximum and a layer may ask for less. Attribution is
   *    per resolution: if no layer asked, the policy's number is what ran; if a layer asked
   *    for more than the policy allows, the policy capped it and the badge says so — that is
   *    the case an operator would otherwise spend an afternoon on.
   */
  origin?: 'policy' | 'ceiling'
  /** The unformatted resolved value, for comparing a `ceiling` row against what a layer asked. */
  raw?: (agent: ResolvedAgent) => unknown
}

/**
 * A set of values as one phrase, through the one `Intl.ListFormat` in the repository
 * (LOC-016). "none" is a state rather than a list and stays its own catalogue entry — an
 * empty `ListFormat` renders the empty string, which reads as a row that failed to load.
 *
 * The risk classes it sometimes joins come from `t.riskLabel`, the administrator's mono
 * spelling. That map is guarded `satisfies Record<RiskClass, string>` in the catalogue, which
 * is the whole point: this row used to swap the underscores in the wire token for spaces,
 * turning a new enum member into a raw identifier on screen with nothing failing anywhere
 * (PRD-27 D-9, LOC-018).
 */
const list = (fmt: RowFormat, t: StudioPack, values: readonly string[]): string =>
  values.length === 0 ? t.preview.none : fmt.formatList(values)

const SECTIONS: Array<{ key: SectionKey; rows: Row[] }> = [
  {
    key: 'instructions',
    rows: [
      {
        key: 'prompt',
        value: (a, fmt, t) => t.preview.charCount(fmt, a.systemPrompt.length),
        reads: (b) => b.systemPrompt
      },
      {
        key: 'prefix',
        value: (a, fmt, t) =>
          a.systemPromptPrefix === '' ? '\u2014' : t.preview.charCount(fmt, a.systemPromptPrefix.length),
        reads: (b) => b.systemPromptPrefix,
        concatenated: true
      }
    ]
  },
  {
    key: 'model',
    rows: [
      {
        key: 'alias',
        value: (a) => a.modelAlias,
        reads: (b) => b.modelAlias,
        origin: 'ceiling',
        raw: (a) => a.modelAlias
      },
      { key: 'answerMode', value: (a) => a.answerMode, reads: (b) => b.answerMode }
    ]
  },
  {
    key: 'tools',
    rows: [
      { key: 'builtin', value: (a, fmt, t) => list(fmt, t, a.tools.builtin), reads: (b) => b.tools?.builtin },
      { key: 'connected', value: (a, fmt, t) => list(fmt, t, a.tools.mcp), reads: (b) => b.tools?.mcp }
    ]
  },
  {
    key: 'retrieval',
    rows: [
      { key: 'strategy', value: (a) => a.retrieval.strategy, origin: 'policy' },
      { key: 'topK', value: (a, fmt) => fmt.formatCount(a.retrieval.topK), origin: 'policy' },
      { key: 'minScore', value: (a, fmt) => fmt.formatNumber(a.retrieval.minScore), origin: 'policy' },
      {
        key: 'sources',
        value: (a, fmt, t) =>
          a.retrieval.scopeIds.length === 0
            ? t.preview.everySource
            : t.preview.pinned(fmt, a.retrieval.scopeIds.length),
        reads: (b) => b.retrieval?.scopeIds
      }
    ]
  },
  {
    key: 'memory',
    rows: [
      { key: 'memoryRead', value: (a, _fmt, t) => (a.memory.read ? t.yes : t.no), reads: (b) => b.memory?.read },
      {
        key: 'memoryWrite',
        value: (a, _fmt, t) => t.editor.memoryWrite[a.memory.write],
        reads: (b) => b.memory?.write
      }
    ]
  },
  {
    key: 'budget',
    rows: [
      {
        key: 'steps',
        value: (a, fmt) => fmt.formatCount(a.budget.maxSteps),
        reads: (b) => b.budget?.maxSteps,
        origin: 'ceiling',
        raw: (a) => a.budget.maxSteps
      },
      {
        key: 'modelCalls',
        value: (a, fmt) => fmt.formatCount(a.budget.maxModelCalls),
        reads: (b) => b.budget?.maxModelCalls,
        origin: 'ceiling',
        raw: (a) => a.budget.maxModelCalls
      },
      {
        key: 'toolCalls',
        value: (a, fmt) => fmt.formatCount(a.budget.maxToolCalls),
        reads: (b) => b.budget?.maxToolCalls,
        origin: 'ceiling',
        raw: (a) => a.budget.maxToolCalls
      },
      {
        key: 'delegations',
        value: (a, fmt) => fmt.formatCount(a.budget.maxDelegations),
        reads: (b) => b.budget?.maxDelegations,
        origin: 'ceiling',
        raw: (a) => a.budget.maxDelegations
      },
      {
        key: 'cost',
        value: (a, fmt) => fmt.formatCost(a.budget.maxCostUsd),
        reads: (b) => b.budget?.maxCostUsd,
        origin: 'ceiling',
        raw: (a) => a.budget.maxCostUsd
      },
      {
        key: 'wallClock',
        value: (a, fmt) => fmt.formatDuration(a.budget.maxWallClockMs),
        reads: (b) => b.budget?.maxWallClockMs,
        origin: 'ceiling',
        raw: (a) => a.budget.maxWallClockMs
      }
    ]
  },
  {
    key: 'humanInLoop',
    rows: [
      {
        key: 'extraRisks',
        value: (a, fmt, t) =>
          list(
            fmt,
            t,
            a.humanInLoop.additionalApprovalRisks.map((risk) => t.riskLabel[risk])
          ),
        origin: 'policy'
      },
      { key: 'rounds', value: (a, fmt) => fmt.formatCount(a.humanInLoop.maxApprovalRounds), origin: 'policy' },
      { key: 'ttl', value: (a, fmt) => fmt.formatDuration(a.humanInLoop.approvalTtlMs), origin: 'policy' }
    ]
  },
  {
    key: 'delegation',
    rows: [
      {
        key: 'remoteAgents',
        value: (a, fmt, t) => list(fmt, t, a.delegation.remoteAgents),
        reads: (b) => b.delegation?.remoteAgents
      },
      {
        key: 'maxDepth',
        value: (a, fmt) => fmt.formatCount(a.delegation.maxDepth),
        reads: (b) => b.delegation?.maxDepth,
        origin: 'ceiling',
        raw: (a) => a.delegation.maxDepth
      },
      {
        key: 'maxParallel',
        value: (a, fmt) => fmt.formatCount(a.delegation.maxParallel),
        reads: (b) => b.delegation?.maxParallel,
        origin: 'ceiling',
        raw: (a) => a.delegation.maxParallel
      }
    ]
  }
]

type ResolvePreviewProps = {
  agentId: string
  /** Every stored layer, in merge order. Used to attribute fields, not to merge. */
  layers: AgentLayer[]
  /** The stored layers' versions; changes after a save so the preview re-resolves. */
  signature: string
}

export const ResolvePreview = ({ agentId, layers, signature }: ResolvePreviewProps) => {
  // The whole catalogue, not just the studio's slice: `describeApiError` looks a refusal up
  // across every domain's sentences, so it takes `Messages` and not a branch of it.
  const messages = useT()
  const t = messages.admin.studio
  const [showPrompt, setShowPrompt] = useState(false)
  const resolution = useResolvedAgent(agentId, signature, true)

  const agent = resolution.data?.agent ?? null
  const contributing = agent
    ? agent.layers.flatMap((entry) => {
        const layer = layers.find((l) => l.scopeId === entry.scopeId)
        return layer ? [layer] : []
      })
    : []

  return (
    <Panel className="flex flex-col overflow-clip lg:sticky lg:top-5 lg:max-h-[calc(100dvh-6rem)]">
      <header className="border-b border-rule px-4 py-3.5">
        <div className="flex items-center justify-between gap-2">
          <span className="eyebrow">{t.preview.title}</span>
          {resolution.isFetching && (
            <span className="readout flex items-center gap-1.5 text-primary">
              <Spinner />
              {t.preview.resolving}
            </span>
          )}
          {!resolution.isFetching && agent?.degraded && (
            <Badge tone="alert">
              <TriangleAlert size={10} strokeWidth={2.6} aria-hidden />
              {t.preview.degraded}
            </Badge>
          )}
        </div>

        <p className="mt-2 text-[12.5px] leading-relaxed text-fg-muted">{t.preview.lead}</p>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3.5">
        {resolution.isError && (
          <Notice title={t.preview.failed} icon={<TriangleAlert size={13} aria-hidden />}>
            {describeApiError(messages, resolution.error)}
          </Notice>
        )}

        {resolution.isLoading && <p className="text-[13px] text-fg-subtle">{t.preview.loading}</p>}

        {!agent && !resolution.isLoading && !resolution.isError && (
          <EmptyState title={t.preview.empty} body={t.preview.emptyBody} />
        )}

        {agent && (
          <>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="eyebrow">{t.preview.definitionEyebrow}</span>
              {agent.layers.map((entry) => (
                <Badge key={entry.scopeId}>{t.preview.versionBadge(entry.version)}</Badge>
              ))}
              {agent.layers.length === 0 && <span className="readout text-fg-subtle">{t.preview.platformOnly}</span>}
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <span className="eyebrow">{t.preview.policyEyebrow}</span>
              <Badge tone="grounded">{t.preview.policyVersion(agent.policy.name, agent.policy.version)}</Badge>
            </div>

            {SECTIONS.map((section) => (
              <section key={section.key} className="mt-4">
                <h3 className="eyebrow border-b border-rule pb-1.5">{t.preview.sections[section.key]}</h3>
                {section.rows.map((row) => (
                  <FieldRow key={row.key} row={row} agent={agent} layers={contributing} />
                ))}
              </section>
            ))}

            <div className="mt-5 border-t border-rule pt-3.5">
              <button
                type="button"
                onClick={() => setShowPrompt((open) => !open)}
                className="text-[13px] text-fg-muted transition-colors hover:text-fg"
              >
                {/* Two entries rather than one word swapped inside a sentence: a phrase
                    needed in two forms gets two catalogue entries (PRD-27 LOC-014). */}
                {showPrompt ? t.preview.hidePrompt : t.preview.showPrompt}
              </button>
              {showPrompt && (
                <pre
                  className={clsx(
                    'readout fade-in mt-2 max-h-72 overflow-auto whitespace-pre-wrap',
                    'rounded-tile border border-rule bg-card-solid p-3 leading-relaxed'
                  )}
                >
                  {[agent.systemPromptPrefix, agent.systemPrompt].filter(Boolean).join('\n\n')}
                </pre>
              )}
            </div>
          </>
        )}
      </div>
    </Panel>
  )
}

const FieldRow = ({ row, agent, layers }: { row: Row; agent: ResolvedAgent; layers: AgentLayer[] }) => {
  const fmt = useI18n()
  const t = fmt.t.admin.studio
  const reads = row.reads
  const source = reads ? inheritedFrom(layers, (body) => reads(body), layers.length) : null
  const alsoEarlier = row.concatenated && reads && layers.filter((layer) => reads(layer.body) !== undefined).length > 1

  // A layer asked for something and got something else: the policy's ceiling won. Said
  // explicitly, because the alternative is an operator reading their own number on the layer
  // and a different number here and having nothing to connect the two.
  const capped = row.origin === 'ceiling' && source !== null && row.raw !== undefined && source.value !== row.raw(agent)

  return (
    <div className="flex items-baseline gap-2 border-b border-rule py-2 last:border-0">
      <span className="eyebrow w-[5.5rem] shrink-0">{t.preview.rows[row.key]}</span>
      <span className="readout min-w-0 flex-1 break-words">{row.value(agent, fmt, t)}</span>
      <span className="shrink-0">
        {row.origin === 'policy' || capped || (source === null && row.origin === 'ceiling') ? (
          <Badge tone="grounded">
            {/* "capped by policy x v3" is its own entry rather than a prefix glued to the
                one beside it: no language places a sentence opening by string addition. */}
            {capped
              ? t.preview.originCapped(agent.policy.name, agent.policy.version)
              : t.preview.originPolicy(agent.policy.name, agent.policy.version)}
          </Badge>
        ) : alsoEarlier ? (
          <Badge>{t.preview.originConcatenated}</Badge>
        ) : source === null ? (
          <Badge>{t.preview.originPlatform}</Badge>
        ) : (
          <Badge>{t.preview.originDefinition}</Badge>
        )}
      </span>
    </div>
  )
}
